import { useEffect, useMemo, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Warning, CheckCircle, Lightning } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

const fmtCur = (n) => "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });

export default function RazorpayPayoutDialog({ open, onOpenChange, creator, onSaved }) {
  const [status, setStatus] = useState(null);   // {configured, mode, key_id_preview}
  const [method, setMethod] = useState("UPI");
  const [form, setForm] = useState({ amount: "", upi_id: "", bank_account_number: "", bank_ifsc: "", beneficiary_name: "", notes: "" });
  const [saving, setSaving] = useState(false);

  const remaining = useMemo(() =>
    Math.max(Number(creator?.final_cost || 0) - Number(creator?.total_paid || 0), 0)
  , [creator]);

  useEffect(() => {
    if (!open) return;
    // Load Razorpay config status + prefill from creator saved details
    api.get("/razorpay/status").then(({ data }) => setStatus(data)).catch(() => setStatus({ configured: false, mode: "simulation" }));
    setForm({
      amount: String(remaining || ""),
      upi_id: creator?.upi_id || "",
      bank_account_number: creator?.bank_account_number || "",
      bank_ifsc: creator?.bank_ifsc || "",
      beneficiary_name: creator?.bank_account_name || creator?.legal_name || creator?.username || "",
      notes: `Payout to @${creator?.username || ""}`.trim(),
    });
    setMethod(creator?.upi_id ? "UPI" : (creator?.bank_account_number ? "IMPS" : "UPI"));
  }, [open, creator, remaining]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    const amt = parseFloat(form.amount || 0) || 0;
    if (amt <= 0) { toast.error("Enter a valid amount"); return; }
    if (method === "UPI" && (!form.upi_id || !form.upi_id.includes("@"))) {
      toast.error("Enter a valid UPI ID (like name@bank)"); return;
    }
    if (method !== "UPI" && (!form.bank_account_number || !form.bank_ifsc)) {
      toast.error("Account number and IFSC are required"); return;
    }
    setSaving(true);
    try {
      const { data } = await api.post(`/creators/${creator.id}/razorpay-payout`, {
        amount: amt,
        method,
        upi_id: form.upi_id.trim(),
        bank_account_number: form.bank_account_number.trim(),
        bank_ifsc: form.bank_ifsc.trim().toUpperCase(),
        beneficiary_name: form.beneficiary_name.trim(),
        notes: form.notes,
      });
      toast.success(
        data.simulation
          ? `Simulated payout logged · ref ${data.payout_id.slice(0, 16)}…`
          : `Payout ${data.payout_status || "queued"} · ${data.payout_id}`
      );
      onSaved?.(data);
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Payout failed");
    } finally { setSaving(false); }
  };

  const isSim = status && !status.configured;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="razorpay-payout-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl flex items-center gap-2">
            <Lightning size={22} weight="fill" className="text-blue-600" />
            Pay now · @{creator?.username}
          </DialogTitle>
          <DialogDescription className="text-xs text-slate-500">
            Sends money through Razorpay Payouts and logs it in your ledger.
          </DialogDescription>
        </DialogHeader>

        {status && (
          <div
            className={`text-[11px] flex items-start gap-2 rounded-md px-3 py-2 ${
              isSim ? "bg-amber-50 border border-amber-200 text-amber-800" : "bg-emerald-50 border border-emerald-200 text-emerald-800"
            }`}
            data-testid="razorpay-mode-banner"
          >
            {isSim ? <Warning size={14} className="mt-0.5 shrink-0" /> : <CheckCircle size={14} className="mt-0.5 shrink-0" weight="fill" />}
            <span>
              {isSim ? (
                <><b>Simulation mode.</b> No real money will move. Add <code>RAZORPAY_KEY_ID</code>, <code>RAZORPAY_KEY_SECRET</code> and <code>RAZORPAY_ACCOUNT_NUMBER</code> to enable live payouts.</>
              ) : (
                <><b>Live mode</b> · key {status.key_id_preview}. This will move real money.</>
              )}
            </span>
          </div>
        )}

        <div className="text-xs text-slate-500">
          Remaining balance:{" "}
          <span className="font-mono text-slate-900 font-semibold">{fmtCur(remaining)}</span>
          {" · "}
          Final cost: <span className="font-mono">{fmtCur(creator?.final_cost)}</span>
        </div>

        <div className="space-y-3">
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">Amount (₹) *</Label>
            <div className="flex gap-2 mt-1">
              <Input
                type="number"
                value={form.amount}
                onChange={(e) => set("amount", e.target.value)}
                placeholder="0"
                data-testid="razorpay-amount-input"
              />
              <Button
                type="button"
                variant="outline"
                onClick={() => set("amount", String(remaining))}
                disabled={!remaining}
                data-testid="razorpay-pay-full-btn"
              >
                Pay full
              </Button>
            </div>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">Method *</Label>
            <div className="flex gap-2 mt-1">
              {["UPI", "IMPS", "NEFT"].map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => setMethod(m)}
                  className={`flex-1 px-3 py-2 rounded-md text-sm border transition-colors ${
                    method === m ? "border-blue-500 bg-blue-50 text-blue-700 font-medium" : "border-slate-200 hover:border-slate-300 text-slate-700"
                  }`}
                  data-testid={`razorpay-method-${m.toLowerCase()}`}
                >
                  {m}
                </button>
              ))}
            </div>
          </div>

          {method === "UPI" ? (
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">UPI ID *</Label>
              <Input
                value={form.upi_id}
                onChange={(e) => set("upi_id", e.target.value)}
                placeholder="creator@ybl"
                className="mt-1"
                data-testid="razorpay-upi-input"
              />
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-2">
              <div className="col-span-2">
                <Label className="text-xs uppercase tracking-wider text-slate-500">Beneficiary name</Label>
                <Input value={form.beneficiary_name} onChange={(e) => set("beneficiary_name", e.target.value)} className="mt-1" data-testid="razorpay-beneficiary-input" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500">Account number *</Label>
                <Input value={form.bank_account_number} onChange={(e) => set("bank_account_number", e.target.value)} className="mt-1 font-mono" data-testid="razorpay-account-input" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500">IFSC *</Label>
                <Input value={form.bank_ifsc} onChange={(e) => set("bank_ifsc", e.target.value.toUpperCase())} className="mt-1 font-mono uppercase" data-testid="razorpay-ifsc-input" />
              </div>
            </div>
          )}

          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">Note (narration ≤ 30 chars)</Label>
            <Textarea
              value={form.notes}
              onChange={(e) => set("notes", e.target.value)}
              rows={2}
              className="mt-1"
              maxLength={30}
              data-testid="razorpay-notes-input"
            />
            <div className="text-[10px] text-slate-400 mt-0.5 text-right">{form.notes.length}/30</div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="razorpay-cancel-btn">
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={saving}
            className="bg-blue-600 hover:bg-blue-700 text-white"
            data-testid="razorpay-submit-btn"
          >
            {saving ? "Sending…" : isSim ? "Simulate payout" : "Pay now"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
